import { audio } from '@/audio-singleton';
import { useEffect, useState } from 'preact/hooks';
import { BottomSheet } from './BottomSheet';
import { gameSignals, type Locale } from '@/bridge/gameSignals';
import { t } from '@/i18n';
import { SHOTS_PER_DAY } from '@/lib/constants';
import type { StatsProvider, DailyStats } from '@/stats/StatsProvider';

type Status = { kind: 'loading' } | { kind: 'empty' } | { kind: 'error' } | { kind: 'data'; data: DailyStats };

export interface StatsOverlayProps {
  open: boolean;
  onClose: () => void;
  provider: StatsProvider;
}

export function StatsOverlay({ open, onClose, provider }: StatsOverlayProps) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [retryNonce, setRetryNonce] = useState(0);
  const date = gameSignals.dateKey.value ?? '';
  const locale = gameSignals.currentLocale.value as Locale;

  useEffect(() => {
    if (!open) return;
    const ctl = new AbortController();
    setStatus({ kind: 'loading' });
    provider.getDailyStats(date, ctl.signal)
      .then((d) => {
        if (ctl.signal.aborted) return;
        setStatus(d ? { kind: 'data', data: d } : { kind: 'empty' });
      })
      .catch((err) => {
        if (ctl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setStatus({ kind: 'error' });
      });
    return () => ctl.abort();
  }, [open, date, retryNonce, provider]);

  const fmtPct = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(n);
  const fmtAvg = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(n);

  return (
    <BottomSheet open={open} onClose={onClose} heightVariant="tall" ariaLabel={t('stats.title')}>
      <div class="px-6 pb-6">
        <p class="font-mono text-[10px] uppercase tracking-[0.22em] text-foil mb-1">
          {t('stats.title')}
        </p>
        <h2 class="font-display text-xl mb-5 text-text">{date}</h2>

        {status.kind === 'loading' && (
          <p class="font-mono text-xs uppercase tracking-wider text-text-dim">{t('stats.loading')}</p>
        )}
        {status.kind === 'empty' && (
          <p class="text-text-2">{t('stats.empty')}</p>
        )}
        {status.kind === 'error' && (
          <div class="space-y-3">
            <p class="text-corner">{t('stats.error')}</p>
            <button
              class="font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-md bg-foil text-bg hover:bg-foil-bright active:scale-95 transition-all"
              onClick={() => { audio().play('tap'); setRetryNonce((n) => n + 1); }}
            >{t('stats.retry')}</button>
          </div>
        )}
        {status.kind === 'data' && (
          <ul class="divide-y divide-foil/15 border-t border-b border-foil/15">
            <Stat
              label={t('stats.todayAverage')}
              value={fmtAvg(status.data.todayAverage)}
              suffix={`/${SHOTS_PER_DAY}`}
            />
            <Stat
              label={t('stats.scoredAtLeast3')}
              value={fmtPct(status.data.scoredHalfOrMorePct)}
              suffix="%"
            />
            <Stat
              label={t('stats.shotLeftFirst')}
              value={fmtPct(status.data.shotLeftFirstPct)}
              suffix="%"
            />
          </ul>
        )}
      </div>
    </BottomSheet>
  );
}

function Stat({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <li class="flex items-baseline justify-between py-3">
      <span class="text-sm text-text-2">{label}</span>
      <span class="font-display tabular-nums text-xl text-text">
        {value}{suffix && <span class="font-mono text-xs ml-0.5 text-text-dim">{suffix}</span>}
      </span>
    </li>
  );
}
